<template>
  <div class="payout-summary-main">
    <div class="left-container">
      <div class="d-flex align-items-center gap-3">
        <div class="filter-buttons">
          <DateTimePicker
            :showTime="false"
            :showMonthOnly="true"
            placeholder="Process Month"
            :value.sync="selectedMonth"
            :isRequired="true"
            style="top: 0px"
            @update:value="getBatchProcesses"
          />
        </div>
        <Button class="mt-1 ml-auto flex-shrink-0" style="width: fit-content" @click="previewClicked"> Preview </Button>
      </div>
      <div class="table-container">
        <TableBox
          :title="`Batch Process History`"
          :tableData="payrollHistory"
          :dataType="payrollHistoryDataType"
          :selectedRow.sync="selectedPayrollBatch"
          :maxSelection="0"
          :hideColumns="['MonthProcessID', 'PayMonth', 'PayYear']"
        />
      </div>
    </div>
    <div class="right-container" style="justify-content: center">
      <DesignerV3
        v-if="showReportPreview"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        :downloadName="downloadFileName"
        ref="designerV3"
      />
      <div v-else class="print-placeholder"> Select a record and click on <strong>Preview</strong> to get started </div>
    </div>
  </div>
</template>

<script>
export default {
  computed: {},
  data: function () {
    return {
      userDetails: null,
      selectedMonth: null,

      payrollHistory: [],
      selectedPayrollBatch: [],
      payrollList: [],
      payMonth: '',
      payYear: '',
      payrollHistoryDataType: {
        'Total Payout': 'number',
        'Total Employer EPF': 'number',
        'Total Employer SOCSO': 'number',
        'Total Employer SIP': 'number',
      },

      showReportPreview: false,
      downloadFileName: '',
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
        options: {
          fileName: '',
          folder: '',
        },
      },

      totalPayout: 0,
      totalBasic: 0,
      totalCommission: 0,
      totalExtraCommission: 0,
      totalExpenses: 0,
      totalEPF: 0,
      totalSOCSO: 0,
      totalAdvPay: 0,
      totalBonus: 0,
      totalViolations: 0,
      totalIncomeTax: 0,
      totalSpecialDay: 0,
      totalTabung: 0,
      totalUnpaidLeave: 0,
      totalAnnualBonus: 0,
      totalOverTime: 0,
      totalSIP: 0,
      totalCoEPF: 0,
      totalCoSOCSO: 0,
      totalCoSIP: 0,
    };
  },
  methods: {
    previewClicked: async function () {
      await this.getPayrollList();
      this.clearTotals();
      this.designerV3ReportData = [];
      this.designerV3ReportFiles = [];

      const reportFiles = [];
      const employees = [];

      reportFiles['companyName'] = this.userDetails['CoName'];
      reportFiles['printDate'] = new Date().formatString('yyyy-MM-dd HH:mm:ss');
      reportFiles['printBy'] = this.userDetails['LoginUserId'];
      reportFiles['payMonth'] = this.payMonth;
      reportFiles['payYear'] = this.payYear;

      let seq = 1;
      for (const p of this.payrollList) {
        const payrollDetails = [];

        this.formatReportData(p, payrollDetails);

        // employee information
        payrollDetails['staffNRIC'] = p['Staff_NRIC'];
        payrollDetails['staffMobile'] = p['Staff_MobileNo'];
        payrollDetails['incTaxCategory'] = p['IncTaxCategory'];
        payrollDetails['bankAccount'] = p['PayBankAccNo'];
        payrollDetails['_seq'] = seq++;

        employees.push(payrollDetails);
      }
      this.$fullscreenLoading();
      reportFiles['employees'] = employees;

      // total fields
      reportFiles['_totalPayout'] = this.$currencyFormat(this.totalPayout);
      reportFiles['_totalBasic'] = this.$currencyFormat(this.totalBasic);
      reportFiles['_totalCommission'] = this.$currencyFormat(this.totalCommission);
      reportFiles['_totalSpecialDay'] = this.$currencyFormat(this.totalSpecialDay);
      reportFiles['_totalExtraCommission'] = this.$currencyFormat(this.totalExtraCommission);
      reportFiles['_totalExpenses'] = this.$currencyFormat(this.totalExpenses);
      reportFiles['_totalAdvPay'] = this.$currencyFormat(this.totalAdvPay);
      reportFiles['_totalBonus'] = this.$currencyFormat(this.totalBonus);
      reportFiles['_totalViolations'] = this.$currencyFormat(this.totalViolations);
      reportFiles['_totalSOCSO'] = this.$currencyFormat(this.totalSOCSO);
      reportFiles['_totalCoSOCSO'] = this.$currencyFormat(this.totalCoSOCSO);
      reportFiles['_totalEPF'] = this.$currencyFormat(this.totalEPF);
      reportFiles['_totalCoEPF'] = this.$currencyFormat(this.totalCoEPF);
      reportFiles['_totalSIP'] = this.$currencyFormat(this.totalSIP);
      reportFiles['_totalCoSIP'] = this.$currencyFormat(this.totalCoSIP);
      reportFiles['_totalTabung'] = this.$currencyFormat(this.totalTabung);
      reportFiles['_totalIncomeTax'] = this.$currencyFormat(this.totalIncomeTax);
      reportFiles['_totalUnpaidLeave'] = this.$currencyFormat(this.totalUnpaidLeave);
      reportFiles['_totalAnnualBonus'] = this.$currencyFormat(this.totalAnnualBonus);
      reportFiles['_totalOverTime'] = this.$currencyFormat(this.totalOverTime);

      this.downloadFileName = `PayrollSummary_${new Date().formatString('yyyyMMddHHmm')}`;
      this.designerV3ReportFiles = ['PayrollBatchSummary.json'];
      this.designerV3ReportData[0] = reportFiles;
      this.showReportPreview = true;
    },
    formatReportData: function (payroll, details) {
      // format data by payroll
      details['staffID'] = payroll['Driver Code'];
      details['staffName'] = payroll['Driver Name'];

      details['nettPay'] = this.$currencyFormat(payroll['Nett Pay']);
      this.totalPayout += parseFloat(payroll['Nett Pay']);

      details['basicPay'] = this.$currencyFormat(payroll['Basic']);
      this.totalBasic += parseFloat(payroll['Basic']);

      details['commission'] = this.$currencyFormat(payroll['Commission']);
      this.totalCommission += parseFloat(payroll['Commission']);

      details['specialDay'] = this.$currencyFormat(payroll['Special Day']);
      this.totalSpecialDay += parseFloat(payroll['Special Day']);

      details['extraInc'] = this.$currencyFormat(payroll['Extra Inc.']);
      this.totalExtraCommission += parseFloat(payroll['Extra Inc.']);

      details['expenses'] = this.$currencyFormat(payroll['Expenses']);
      this.totalExpenses += parseFloat(payroll['Expenses']);

      details['advancePay'] = this.$currencyFormat(payroll['Loan Deduct']);
      this.totalAdvPay += parseFloat(payroll['Loan Deduct']);

      details['bonus'] = this.$currencyFormat(payroll['Bonus']);
      this.totalBonus += parseFloat(payroll['Bonus']);

      details['violations'] = this.$currencyFormat(payroll['Violations']);
      this.totalViolations += parseFloat(payroll['Violations']);

      details['SOCSO'] = this.$currencyFormat(payroll['SOCSO']);
      this.totalSOCSO += parseFloat(payroll['SOCSO']);

      details['coSOCSO'] = this.$currencyFormat(payroll['Employer SOCSO']);
      this.totalCoSOCSO += parseFloat(payroll['Employer SOCSO']);

      details['EPF'] = this.$currencyFormat(payroll['EPF']);
      this.totalEPF += parseFloat(payroll['EPF']);

      details['coEPF'] = this.$currencyFormat(payroll['Employer EPF']);
      this.totalCoEPF += parseFloat(payroll['Employer EPF']);

      details['SIP'] = this.$currencyFormat(payroll['SIP']);
      this.totalSIP += parseFloat(payroll['SIP']);

      details['coSIP'] = this.$currencyFormat(payroll['Employer SIP']);
      this.totalCoSIP += parseFloat(payroll['Employer SIP']);

      details['tabung'] = this.$currencyFormat(payroll['Tabung']);
      this.totalTabung += parseFloat(payroll['Tabung']);

      details['incomeTax'] = this.$currencyFormat(payroll['Income Tax']);
      this.totalIncomeTax += parseFloat(payroll['Income Tax']);

      details['unpaidLeave'] = this.$currencyFormat(payroll['Unpaid Leave']);
      this.totalUnpaidLeave += parseFloat(payroll['Unpaid Leave']);

      details['annualBonus'] = this.$currencyFormat(payroll['Annual Bonus']);
      this.totalAnnualBonus += parseFloat(payroll['Annual Bonus']);

      details['overTime'] = this.$currencyFormat(payroll['OverTime']);
      this.totalOverTime += parseFloat(payroll['OverTime']);
    },
    clearTotals: function () {
      this.totalPayout = 0;
      this.totalBasic = 0;
      this.totalCommission = 0;
      this.totalExtraCommission = 0;
      this.totalExpenses = 0;
      this.totalEPF = 0;
      this.totalSOCSO = 0;
      this.totalAdvPay = 0;
      this.totalBonus = 0;
      this.totalViolations = 0;
      this.totalIncomeTax = 0;
      this.totalSpecialDay = 0;
      this.totalTabung = 0;
      this.totalUnpaidLeave = 0;
      this.totalAnnualBonus = 0;
      this.totalOverTime = 0;
      this.totalSIP = 0;
      this.totalCoSOCSO = 0;
      this.totalCoEPF = 0;
      this.totalCoSIP = 0;
    },
    getBatchProcesses: async function (month) {
      this.$fullscreenLoading('Getting Batch Process History');
      this.payrollHistory = [];
      this.payMonth = month.formatString('MM');
      this.payYear = month.formatString('yyyy');

      const dataToPost = {
        system: 'HMS',
        month: this.payMonth,
        year: this.payYear,
      };
      this.payrollHistory = await this.$apiCallV2('/DriverPayroll/GetBatchPayrollHistory', dataToPost);
      this.$fullscreenLoading();
    },
    getPayrollList: async function () {
      // load details of all payrolls in month
      this.$fullscreenLoading('Loading Payrolls');
      this.payrollList = [];
      const dataToPost = {
        month: this.payMonth,
        year: this.payYear,
        system: 'HMS',
        getallbatches: '1',
      };

      this.payrollList = await this.$apiCallV2('/DriverPayroll/GetBatchPayrollListing', dataToPost);
      this.$fullscreenLoading();
    },
    async sasControlFunction() {
      this.$fullscreenLoading('Checking Security Access');
      const sasControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=HMS&page=Driver Batch Payroll`);
      if (sasControl.find((k) => k['Control'] == 'MnuPrintPaySummary')) {
        // redirect to home page if no access
        await this.$showAlert('error', 'Access Denied', 'You do not have permission to access this page.');
        this.$router.push(`/Home/${this.$route.params['user']}`);
      }
    },
  },
  async mounted() {
    await this.sasControlFunction();
    this.$fullscreenLoading('Getting Details');
    this.userDetails = await this.$apiCallV2(`/Login/GetDetails`);

    if (this.$route.query['date']) {
      this.selectedMonth = new Date(this.$route.query['date']);
      await this.getBatchProcesses(this.selectedMonth);
    }
    this.$fullscreenLoading();
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.payout-summary-main {
  font-size: 12px;
  padding: 10px 45px;
  width: 100%;
  height: 100%;
  display: flex;
  gap: 6px;

  > .left-container,
  .right-container {
    height: 100%;
    width: 100%;
    padding: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
    display: flex;
    flex-direction: column;
    gap: 6px;

    > .table-container {
      overflow: auto;
      height: 100%;
    }

    > .print-placeholder {
      text-align: center;
      font-size: 1.25rem;
      width: 100%;
    }
  }
}
.filter-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
